import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Mail, Phone, Shield, ChevronRight, HelpCircle, FileText, Globe } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    toast.success('Logout realizado');
    navigate('/login');
  };

  const isPartner = ['kiosk_owner', 'kiosk_staff'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="profile-page">
      <TopBar title="Perfil" />

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* User Info */}
        <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-xl font-bold">{user?.full_name}</h2>
              <p className="text-white/80 text-sm">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">
                {user?.role === 'customer' && 'Cliente'}
                {user?.role === 'kiosk_owner' && 'Dono de Quiosque'}
                {user?.role === 'admin' && 'Administrador'}
              </span>
            </div>
          </div>
        </div>

        {/* Quick access for partners/admin */}
        {(isPartner || isAdmin) && (
          <div className="space-y-2">
            {isPartner && (
              <button
                data-testid="go-to-partner"
                onClick={() => navigate('/partner/dashboard')}
                className="w-full bg-white rounded-xl shadow-sm border border-border p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
              >
                <Shield className="w-6 h-6 text-primary" />
                <div className="flex-1 text-left">
                  <p className="font-semibold">Painel do Quiosque</p>
                  <p className="text-xs text-muted-foreground">Gerencie pedidos</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="go-to-admin"
                onClick={() => navigate('/admin/dashboard')}
                className="w-full bg-white rounded-xl shadow-sm border border-border p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
              >
                <Shield className="w-6 h-6 text-accent" />
                <div className="flex-1 text-left">
                  <p className="font-semibold">Admin Beach Pay</p>
                  <p className="text-xs text-muted-foreground">Painel administrativo</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}

        {/* Info Cards */}
        <div className="bg-white rounded-xl shadow-sm border border-border divide-y divide-border">
          <div className="p-4 flex items-center gap-3">
            <Mail className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">E-mail</p>
              <p className="font-semibold text-sm">{user?.email}</p>
            </div>
          </div>
          <div className="p-4 flex items-center gap-3">
            <Phone className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Telefone</p>
              <p className="font-semibold text-sm">{user?.phone}</p>
            </div>
          </div>
        </div>

        {/* Language */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-4 flex items-center gap-3">
          <Globe className="w-5 h-5 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Idioma</p>
            <p className="font-semibold text-sm">Selecione o idioma do app</p>
          </div>
          <LanguageSwitcher compact />
        </div>

        {/* Help & Legal */}
        <div className="bg-white rounded-xl shadow-sm border border-border divide-y divide-border">
          <button className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors">
            <HelpCircle className="w-5 h-5 text-muted-foreground" />
            <span className="flex-1 text-left font-semibold text-sm">Ajuda e Suporte</span>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
          <button className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors">
            <FileText className="w-5 h-5 text-muted-foreground" />
            <span className="flex-1 text-left font-semibold text-sm">Termos de Uso</span>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
          <button className="w-full p-4 flex items-center gap-3 hover:bg-muted/50 transition-colors">
            <Shield className="w-5 h-5 text-muted-foreground" />
            <span className="flex-1 text-left font-semibold text-sm">Política de Privacidade</span>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Logout */}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full p-4 bg-destructive/10 text-destructive rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-destructive/20 transition-colors active:scale-95"
        >
          <LogOut className="w-5 h-5" />
          Sair
        </button>

        <p className="text-center text-xs text-muted-foreground pt-4">
          Beach Pay v1.0.0
        </p>
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;
